import { useMemo } from 'react';

/**
 * Returns a memoized array of strings or numbers that are sorted.
 * This is useful for avoiding unnecessary re-renders and ensuring that the array is always
 * sorted.
 * @param array - The array of strings or numbers to memoize.
 * @returns The memoized array of strings or numbers.
 */
export const useMemoizedArray = <T extends string | number>(
  array: T[] | null | undefined,
): T[] => {
  const { sortedArrayString, firstElementIsNumber } = useMemo(() => {
    if (!array) {
      return { sortedArrayString: null, firstElementIsNumber: false };
    }

    const sortedArrayString = array.sort().join(',');
    const firstElementIsNumber =
      array && array.length > 0 ? typeof array[0] === 'number' : false;
    return { sortedArrayString, firstElementIsNumber };
  }, [array]);

  const memoizedArray = useMemo(() => {
    if (!sortedArrayString) {
      return [];
    }
    const elements = sortedArrayString.split(',');

    if (firstElementIsNumber) {
      return elements.map(Number) as T[];
    }
    return elements as T[];
  }, [sortedArrayString, firstElementIsNumber]);

  return memoizedArray;
};
